import { RefObject, useEffect, useMemo } from 'react'
import { MonacoEditorConfig, EditorIframeMessage } from '@/types'
import { ConfigProps, UseMessageListenerArgs } from './CodeEditor.types'
import { EditorEvent } from '@/config/editor'
import { useTheme } from '@/providers/theme'
import { Theme } from '@/config/app'

const usePostMessage = (editorId: string, ref: RefObject<HTMLIFrameElement>) => (type: EditorEvent, message: any) => {
  ref.current?.contentWindow?.postMessage({
    message: JSON.stringify(message),
    id: editorId,
    type,
  })
}
export const useEditorConfig = (
  editorId: string,
  ref: RefObject<HTMLIFrameElement>,
  props: ConfigProps
): MonacoEditorConfig => {
  const postMessage = usePostMessage(editorId, ref)
  const { theme } = useTheme()

  const value = useMemo<MonacoEditorConfig>(
    () => ({
      ...props.config,
      theme: theme === Theme.Light ? 'light' : props.theme ?? 'dark',
      onModelChange: !!props.onModelChange,
      onScrollChange: !!props.onScrollChange,
      onSelectionChanged: !!props.onSelectionChanged,
      openAddSnippetModal: !!props.openAddSnippetModal,
      onCursorChanged: !!props.onCursorChanged,
    }),
    [
      theme,
      props.config,
      props.theme,
      props.onModelChange,
      props.onScrollChange,
      props.onSelectionChanged,
      props.openAddSnippetModal,
      props.onCursorChanged,
    ]
  )

  useEffect(() => {
    postMessage(EditorEvent.ConfigChange, value)
  }, [value])

  return value
}

export const useParentValue = (
  editorId: string,
  ref: RefObject<HTMLIFrameElement>,
  parentValue: string | undefined
) => {
  const postMessage = usePostMessage(editorId, ref)

  useEffect(() => {
    if (typeof parentValue === 'undefined') return
    postMessage(EditorEvent.CodeChange, parentValue)
  }, [parentValue])
}

export const useMessageListener = ({ editorId, ref, props, config }: UseMessageListenerArgs) => {
  const postMessage = usePostMessage(editorId, ref)

  useEffect(() => {
    const handler = (msg: MessageEvent<EditorIframeMessage>) => {
      const m = msg.data
      if (m.editorId !== editorId) return
      switch (m.type) {
        case EditorEvent.ModelChange:
          props.onModelChange?.(JSON.parse(m.message))
          break
        case EditorEvent.ScrollChange:
          props.onScrollChange?.(JSON.parse(m.message))
          break
        case EditorEvent.SelectionChange:
          props.onSelectionChanged?.(JSON.parse(m.message))
          break
        // case 'openAddSnippetModal':
        //   openAddSnippetModal?.()
        //   break
        case EditorEvent.CursorChange:
          props.onCursorChanged?.(m.message)
          break
        case EditorEvent.Ready:
          postMessage(EditorEvent.Ready, props.initialValue)
          break

        case EditorEvent.ConfigChange:
          postMessage(EditorEvent.ConfigChange, config)
          break
      }
    }

    window.addEventListener('message', handler)

    return () => window.removeEventListener('message', handler)
  }, [])
}
